import { useMemo } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Group from 'components/utils/Group'
import LineArrowToRightIcon from 'LuxKit/Icons/line/LineArrowToRightIcon'
import LineLeftArrowToLeftIcon from 'LuxKit/Icons/line/LineLeftArrowToLeftIcon'
import LineDrinksIcon from 'LuxKit/Icons/line/LineDrinksIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import TextLink from 'LuxKit/TextLink'
import { FormattedMessage, useIntl } from 'react-intl'
import ThingsToKnowLayout from 'components/Common/ThingsToKnow/ThingsToKnowLayout'
import type { ThingsToKnowLayoutMode, ThingsToKnowRow } from 'components/Common/ThingsToKnow/ThingsToKnowLayout'

import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import { FLASH_THINGS_TO_KNOW_ROW_KEYS as K } from 'tripPlanner/assistant/surfaces/offerPage/offerPageThingsToKnowRowKeys'
import { getAuthoredTransferDetails } from 'lib/offer/getOfferTransferDetails'

interface Props {
  offer: App.Offer
  mode?: ThingsToKnowLayoutMode
}

function ThingsToKnow(props: Props) {
  const { offer, mode } = props
  const intl = useIntl()

  const {
    finePrint,
    checkInTime,
    checkOutTime,
    inclusions,
    inclusionsHeading,
    property,
    structuredFinePrint,
  } = offer

  const inclusionsContent = inclusions ?? structuredFinePrint?.inclusionNotes
  const baseContent = (inclusionsContent ?? '').trim()
  const cancellationText = structuredFinePrint?.cancellationText
  const dateChangesText = structuredFinePrint?.dateChangesText
  const finePrintText = structuredFinePrint?.finePrint ?? finePrint
  const authoredTransferDetails = getAuthoredTransferDetails(offer)
  const loyaltyInclusions = useLoyaltyBenefitInclusions(offer)
  const inclusionsContentWithLoyalty = useMemo(() => {
    if (!loyaltyInclusions.length) return baseContent
    const loyaltyLines = loyaltyInclusions.map((i) => `- ${i.description}`).join('\n')
    return baseContent ? `${baseContent}\n${loyaltyLines}` : loyaltyLines
  }, [baseContent, loyaltyInclusions])

  const checkInLabel = intl.formatMessage({
    id: 'GO5kgSlhIoJriWghX3-v',
    defaultMessage: 'Check-in from',
  })
  const checkOutLabel = intl.formatMessage({
    id: 'YGZtng1KK4_nCY-5nTbU',
    defaultMessage: 'Check-out by',
  })
  const inclusionsLabel = intl.formatMessage({
    id: 'jC65d7l0oyo06NoTAVcm',
    defaultMessage: 'Inclusions',
  })
  const inclusionsRowLabel = (inclusionsHeading?.trim().length ?? 0) > 1 ? (inclusionsHeading ?? inclusionsLabel) : inclusionsLabel
  const cancellationLabel = intl.formatMessage({
    id: 'EnAVJQ2V_oME-qJ91Yme',
    defaultMessage: 'Cancellation policy',
  })
  const dateChangesLabel = intl.formatMessage({
    id: 'KXQYH9vHWqc2ztt_2Qlj',
    defaultMessage: 'Date changes',
  })
  const finePrintLabel = intl.formatMessage({
    id: 'XyfySkseacGwZyjkSAqr',
    defaultMessage: 'Fine print',
  })
  const transferDetailsLabel = intl.formatMessage({
    id: 'merjwLCyELGKZ4xDlS8c',
    defaultMessage: 'Important Information',
  })
  const taxesAndFeesLabel = intl.formatMessage({
    id: 'BGtki6GKP-J40skrtqA_',
    defaultMessage: 'Taxes & fees:',
  })

  const cancellationContent = useMemo(() => (
    <Group direction="vertical" gap={8}>
      {offer.type === OFFER_TYPE_ALWAYS_ON && (
        <BodyText variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="Ot0T6xeaSmIMYsZVaPIv"
            defaultMessage="Please note cancellation policies vary depending on the rate selected, please choose your package carefully. See full <link>booking terms and conditions</link>"
            
            values={{
              link: (chunks) => <TextLink weight="regular" to="/booking-policies" target="_blank">{chunks}</TextLink>,
            }}
          />
        </BodyText>
      )}
      {offer.type !== OFFER_TYPE_ALWAYS_ON && cancellationText && (
        <MarkdownRender type="compact" fontSize="medium" content={cancellationText} />
      )}
    </Group>
  ), [offer.type, cancellationText])

  const finePrintContent = useMemo(() => (
    <Group direction="vertical" gap={12}>
      {!!property?.taxesAndFeesContent && (
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">{taxesAndFeesLabel}</BodyText>
          <MarkdownRender type="compact" fontSize="medium" content={property.taxesAndFeesContent} />
        </Group>
      )}
      <MarkdownRender type="compact" fontSize="medium" content={finePrintText} />
    </Group>
  ), [property?.taxesAndFeesContent, taxesAndFeesLabel, finePrintText])

  const transferDetailsContent = useMemo(() => (
    <Group direction="vertical" gap={8}>
      {!!authoredTransferDetails?.heading && (
        <BodyText variant="medium" weight="bold">{authoredTransferDetails.heading}</BodyText>
      )}
      {!!authoredTransferDetails?.body && (
        <MarkdownRender type="compact" fontSize="medium" content={authoredTransferDetails.body} />
      )}
    </Group>
  ), [authoredTransferDetails])

  const rows = useMemo<Array<ThingsToKnowRow>>(() => [
    ...(checkInTime ? [{
      key: K.checkIn,
      icon: <LineArrowToRightIcon size="S" colour="normal-contrast" />,
      label: checkInLabel,
      content: checkInTime,
      collapsable: false,
    }] : []),
    ...(checkOutTime ? [{
      key: K.checkOut,
      icon: <LineLeftArrowToLeftIcon size="S" colour="normal-contrast" />,
      label: checkOutLabel,
      content: checkOutTime,
      collapsable: false,
    }] : []),
    ...(inclusionsContentWithLoyalty ? [{
      key: K.inclusions,
      icon: <LineDrinksIcon size="S" colour="normal-contrast" />,
      label: inclusionsRowLabel,
      content: <MarkdownRender type="compact" fontSize="medium" content={inclusionsContentWithLoyalty} />,
      collapsable: true,
    }] : []),
    ...((offer.type === OFFER_TYPE_ALWAYS_ON || cancellationText) ? [{
      key: K.cancellation,
      icon: <LineRefundIcon size="S" colour="normal-contrast" />,
      label: cancellationLabel,
      content: cancellationContent,
      collapsable: true,
    }] : []),
    ...(dateChangesText ? [{
      key: K.dateChanges,
      icon: <LineCalendarIcon size="S" colour="normal-contrast" />,
      label: dateChangesLabel,
      content: <MarkdownRender type="compact" fontSize="medium" content={dateChangesText} />,
      collapsable: true,
    }] : []),
    ...(finePrintText ? [{
      key: K.finePrint,
      icon: <LineInfoCircleIcon size="S" colour="normal-contrast" />,
      label: finePrintLabel,
      content: finePrintContent,
      collapsable: true,
    }] : []),
    ...(authoredTransferDetails ? [{
      key: K.transferDetails,
      icon: <LineInfoCircleIcon size="S" colour="normal-contrast" />,
      label: transferDetailsLabel,
      content: transferDetailsContent,
      collapsable: true,
    }] : []),
  ], [
    checkInTime, checkInLabel, checkOutTime, checkOutLabel,
    inclusionsContentWithLoyalty, inclusionsRowLabel,
    offer.type, cancellationText, cancellationLabel, cancellationContent,
    dateChangesText, dateChangesLabel,
    finePrintText, finePrintLabel, finePrintContent,
    authoredTransferDetails, transferDetailsLabel, transferDetailsContent,
  ])

  return <ThingsToKnowLayout rows={rows} mode={mode} />
}

export default ThingsToKnow
